import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, GitCompare, X, CheckSquare, Square, FolderPlus } from 'lucide-react';
import { listImages, setFavorite, setRating } from '../api/images';
import { getShoot } from '../api/shoots';
import { useAuth } from '../hooks/useAuth';
import { useFilterStore } from '../stores/filterStore';
import { useKeyboard } from '../hooks/useKeyboard';
import { saveScrollPosition, useScrollRestoration } from '../hooks/useScrollRestoration';
import { useWorkspaceId } from '../hooks/useWorkspace';
import { FilterBar } from '../components/ui/FilterBar';
import { CopyLinksButton } from '../components/ui/CopyLinksButton';
import { DownloadZipButton } from '../components/ui/DownloadZipButton';
import { useScrollDirection } from '../hooks/useScrollDirection';
import { MasonryGrid } from '../components/gallery/MasonryGrid';
import { ImageCard } from '../components/gallery/ImageCard';
import { AddToSetPopover } from '../components/sets/AddToSetPopover';
import { SearchPill } from '../components/ui/SearchPill';
import type { ImageSummary, UserName } from '../api/types';

export default function Gallery() {
  const { shootId } = useParams<{ shootId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const filters = useFilterStore();
  const workspaceId = useWorkspaceId();

  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [focusIdx, setFocusIdx] = useState<number | null>(null);
  const [addSetOpen, setAddSetOpen] = useState(false);
  const addSetBtnRef = useRef<HTMLButtonElement>(null);
  // Add-to-set från enskild bild (hover-ikon) — när satt overrides den
  // selection-mode-id:erna i popovern.
  const [singleAddImageId, setSingleAddImageId] = useState<string | null>(null);

  // Auto-hide för rad 2 (filter) + rad 3 (select/copy/download) vid scroll ner.
  const toolbarHidden = useScrollDirection(120);

  const filtersForQuery = useMemo(
    () => ({ stars: filters.stars, user: filters.user, favorites: filters.favorites }),
    [filters.stars, filters.user, filters.favorites],
  );

  const shootQuery = useQuery({
    queryKey: ['shoot', shootId],
    queryFn: () => getShoot(shootId!),
    enabled: !!shootId,
  });

  const imagesQuery = useQuery({
    queryKey: ['shoot', shootId, 'images', filtersForQuery],
    queryFn: () => listImages(shootId!, filtersForQuery),
    enabled: !!shootId,
  });

  const images = imagesQuery.data ?? [];
  const shoot = shootQuery.data;

  // ── Scroll restoration ────────────────────────────────
  const findImageEl = useCallback((imageId: string) => {
    return document.querySelector<HTMLElement>(`[data-image-id="${imageId}"]`);
  }, []);
  useScrollRestoration({
    scope: shootId,
    ready: images.length > 0,
    getElementForImage: findImageEl,
  });

  // ── Navigation till detail som sparar scroll ──────────
  function openDetail(imageId: string) {
    if (shootId) saveScrollPosition(shootId, imageId);
    navigate(`/shoots/${shootId}/images/${imageId}`);
  }

  // ── Keyboard shortcuts ────────────────────────────────
  useKeyboard((e) => {
    if (!images.length) return;
    const cur = focusIdx ?? 0;

    // Cmd+K → search
    if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
      e.preventDefault();
      navigate('/search');
      return;
    }

    if (e.key === 'j' || e.key === 'J' || e.key === 'ArrowRight') {
      e.preventDefault();
      setFocusIdx(Math.min(cur + 1, images.length - 1));
    } else if (e.key === 'k' || e.key === 'K' || e.key === 'ArrowLeft') {
      e.preventDefault();
      setFocusIdx(Math.max(cur - 1, 0));
    } else if (e.key === ' ' || e.key === 'Spacebar') {
      e.preventDefault();
      const photo = images[focusIdx ?? 0];
      if (photo) openDetail(photo.id);
    } else if (e.key === 'f' || e.key === 'F') {
      e.preventDefault();
      const photo = images[focusIdx ?? 0];
      if (photo) toggleFavorite(photo);
    } else if (/^[1-5]$/.test(e.key)) {
      e.preventDefault();
      const photo = images[focusIdx ?? 0];
      if (photo) handleRating(photo, parseInt(e.key, 10));
    } else if (e.key === 's' || e.key === 'S') {
      setSelectMode((m) => !m);
      setSelected(new Set());
    } else if (e.key === '[') {
      filters.setColumns(Math.max(1, filters.columns - 1) as 1 | 2 | 3 | 4);
    } else if (e.key === ']') {
      filters.setColumns(Math.min(4, filters.columns + 1) as 1 | 2 | 3 | 4);
    } else if (e.key === 'Escape') {
      if (addSetOpen) {
        setAddSetOpen(false);
      } else if (selectMode) {
        setSelectMode(false);
        setSelected(new Set());
      } else {
        navigate('/');
      }
    }
  });

  // ── Mutations ─────────────────────────────────────────
  const ratingMutation = useMutation({
    mutationFn: ({ imageId, stars }: { imageId: string; stars: number }) =>
      setRating(imageId, stars),
    onMutate: async ({ imageId, stars }) => {
      if (!user) return;
      await queryClient.cancelQueries({
        queryKey: ['shoot', shootId, 'images', filtersForQuery],
      });
      const prev = queryClient.getQueryData<ImageSummary[]>([
        'shoot',
        shootId,
        'images',
        filtersForQuery,
      ]);
      if (prev) {
        queryClient.setQueryData<ImageSummary[]>(
          ['shoot', shootId, 'images', filtersForQuery],
          prev.map((img) =>
            img.id === imageId
              ? { ...img, ratings: { ...img.ratings, [user.name]: stars } }
              : img,
          ),
        );
      }
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) {
        queryClient.setQueryData(
          ['shoot', shootId, 'images', filtersForQuery],
          ctx.prev,
        );
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ['shoot', shootId, 'images'],
      });
    },
  });

  const favoriteMutation = useMutation({
    mutationFn: ({ imageId, favorite }: { imageId: string; favorite: boolean }) =>
      setFavorite(imageId, favorite),
    onMutate: async ({ imageId, favorite }) => {
      if (!user) return;
      await queryClient.cancelQueries({
        queryKey: ['shoot', shootId, 'images', filtersForQuery],
      });
      const prev = queryClient.getQueryData<ImageSummary[]>([
        'shoot',
        shootId,
        'images',
        filtersForQuery,
      ]);
      if (prev) {
        queryClient.setQueryData<ImageSummary[]>(
          ['shoot', shootId, 'images', filtersForQuery],
          prev.map((img) =>
            img.id === imageId
              ? { ...img, favorites: { ...img.favorites, [user.name]: favorite } }
              : img,
          ),
        );
      }
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) {
        queryClient.setQueryData(
          ['shoot', shootId, 'images', filtersForQuery],
          ctx.prev,
        );
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ['shoot', shootId, 'images'],
      });
    },
  });

  function handleRating(photo: ImageSummary, stars: number) {
    if (!user) return;
    const userName = user.name as UserName;
    const current = photo.ratings?.[userName] ?? 0;
    ratingMutation.mutate({
      imageId: photo.id,
      stars: current === stars ? 0 : stars,
    });
  }

  function toggleFavorite(photo: ImageSummary) {
    if (!user) return;
    const userName = user.name as UserName;
    const current = photo.favorites?.[userName] ?? false;
    favoriteMutation.mutate({ imageId: photo.id, favorite: !current });
  }

  function toggleSelect(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  // Reset focus när bildlistan ändras
  useEffect(() => {
    setFocusIdx(null);
  }, [shootId, filtersForQuery]);

  const selectedImageIds = useMemo(() => Array.from(selected), [selected]);

  return (
    <div className="min-h-screen min-h-[100dvh]">
      {/* Top nav — back/breadcrumb vänster, global search i mitten, ev. actions höger */}
      <nav className="sticky top-0 z-50 flex items-center gap-3 sm:gap-5 px-4 sm:px-7 py-3 bg-loupe-bg/85 backdrop-blur-xl border-b border-loupe-border h-[58px]">
        <div className="flex items-center gap-3 sm:gap-5 min-w-0 flex-shrink">
          <Link
            to="/"
            className="text-loupe-text-3 hover:text-loupe-text text-xs uppercase tracking-[0.1em] py-2 inline-flex items-center gap-1.5 flex-shrink-0"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Hub</span>
          </Link>
          <div className="flex items-center gap-3 text-sm min-w-0">
            <span className="hidden sm:inline text-loupe-text-3 text-xs uppercase tracking-[0.1em]">
              Shoot
            </span>
            <span className="hidden sm:inline text-loupe-text-3">/</span>
            <span className="font-display font-semibold text-base truncate">
              {shoot?.name ?? '…'}
            </span>
            {shoot?.imageCount != null ? (
              <span className="hidden md:inline text-loupe-text-3 text-xs uppercase tracking-[0.1em] flex-shrink-0">
                · {shoot.imageCount} frames
              </span>
            ) : null}
          </div>
        </div>

        {/* Global search — fyller spacer till höger */}
        <div className="flex-1 flex items-center justify-end min-w-0">
          <SearchPill />
        </div>
      </nav>

      {/* Sticky toolbar (rad 2 + 3): filter + select/copy/download. Auto-hide vid scroll ner. */}
      <div
        className={[
          'sticky top-[58px] z-[45] transition-transform duration-200 ease-out',
          toolbarHidden ? '-translate-y-full' : 'translate-y-0',
        ].join(' ')}
      >
        <FilterBar />

        <div className="flex items-center gap-2 flex-wrap px-4 sm:px-7 py-2.5 bg-loupe-bg/85 backdrop-blur-xl border-b border-loupe-border">
          <button
            onClick={() => {
              setSelectMode((m) => !m);
              if (selectMode) setSelected(new Set());
            }}
            className={[
              'inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs min-h-[36px] transition-colors',
              'border',
              selectMode
                ? 'bg-henrik text-white border-henrik'
                : 'bg-loupe-bg-2 text-loupe-text border-loupe-border hover:border-loupe-text-2',
            ].join(' ')}
          >
            {selectMode ? <CheckSquare className="w-3.5 h-3.5" /> : <Square className="w-3.5 h-3.5" />}
            {selectMode ? `${selected.size} selected` : 'Select'}
          </button>

          <span className="text-[11px] tracking-[0.1em] uppercase text-loupe-text-3 tabular-nums">
            {imagesQuery.isLoading
              ? '…'
              : `${images.length} ${images.length === 1 ? 'image' : 'images'}`}
          </span>

          <div className="hidden sm:flex flex-1" />

          <CopyLinksButton
            imageIds={
              selectMode && selected.size > 0
                ? images.filter((i) => selected.has(i.id)).map((i) => i.id)
                : images.map((i) => i.id)
            }
          />

          <DownloadZipButton
            imageIds={
              selectMode && selected.size > 0
                ? images.filter((i) => selected.has(i.id)).map((i) => i.id)
                : images.map((i) => i.id)
            }
            filenameBase={shoot?.name ? `loupe-${shoot.name.replace(/\s+/g, '-').toLowerCase()}` : 'loupe-images'}
          />
        </div>
      </div>

      {/* Gallery body */}
      <div className="px-2.5 sm:px-7 pt-4 pb-32 relative">
        {imagesQuery.isLoading ? (
          <SkeletonMasonry />
        ) : imagesQuery.isError ? (
          <ErrorState onRetry={() => imagesQuery.refetch()} />
        ) : images.length === 0 ? (
          <EmptyState />
        ) : (
          <MasonryGrid>
            {images.map((image, idx) => (
              <div
                key={image.id}
                className="relative"
                data-image-id={image.id}
                onMouseEnter={() => setFocusIdx(idx)}
                data-aspect={
                  image.width && image.height
                    ? image.width / image.height
                    : 0.66
                }
              >
                {selectMode ? (
                  <button
                    type="button"
                    onClick={() => toggleSelect(image.id)}
                    className="block w-full text-left"
                    aria-pressed={selected.has(image.id)}
                  >
                    <SelectableImageCard
                      image={image}
                      shootId={shootId!}
                      selected={selected.has(image.id)}
                    />
                  </button>
                ) : (
                  <ImageCard
                    image={image}
                    shootId={shootId!}
                    index={idx}
                    isFocused={focusIdx === idx}
                    onOpen={openDetail}
                    onAddToSet={(id) => {
                      setSingleAddImageId(id);
                      setAddSetOpen(true);
                    }}
                  />
                )}
              </div>
            ))}
          </MasonryGrid>
        )}
      </div>

      {/* Action bar — visas när minst en bild valts */}
      {selectMode && selected.size > 0 ? (
        <ActionBar
          count={selected.size}
          onClear={() => setSelected(new Set())}
          onCompare={() => {
            const idsParam = Array.from(selected).join(',');
            navigate(`/shoots/${shootId}/compare?ids=${idsParam}`);
          }}
          onAddToSet={() => setAddSetOpen(true)}
          addSetBtnRef={addSetBtnRef}
        />
      ) : null}

      {/* Add to set popover — antingen en enskild bild (hover-ikon) eller
          selection mode-urvalet. */}
      {addSetOpen && workspaceId
        ? (() => {
            const targetIds = singleAddImageId
              ? [singleAddImageId]
              : selectedImageIds;
            if (targetIds.length === 0) return null;
            return (
              <AddToSetPopover
                workspaceId={workspaceId}
                imageIds={targetIds}
                onClose={() => {
                  setAddSetOpen(false);
                  setSingleAddImageId(null);
                }}
                onAdded={() => {
                  setAddSetOpen(false);
                  setSingleAddImageId(null);
                }}
                anchor="bottom"
                anchorRect={addSetBtnRef.current?.getBoundingClientRect() ?? null}
              />
            );
          })()
        : null}
    </div>
  );
}

// ─── Sub-komponenter ───────────────────────────────────────────

function SelectableImageCard({
  image,
  shootId: _shootId,
  selected,
}: {
  image: ImageSummary;
  shootId: string;
  selected: boolean;
}) {
  return (
    <div
      className={[
        'relative overflow-hidden rounded-md bg-loupe-bg-2 cursor-pointer transition-transform duration-200',
        selected ? 'scale-[0.96] ring-2 ring-henrik shadow-[0_0_24px_-4px_rgba(95,168,211,0.5)]' : '',
      ].join(' ')}
    >
      <img
        src={`/api/images/${image.id}/file?size=preview`}
        alt={image.filename}
        loading="lazy"
        decoding="async"
        width={image.width}
        height={image.height}
        className="w-full h-auto block"
        style={{
          aspectRatio: image.width && image.height ? `${image.width} / ${image.height}` : '2 / 3',
        }}
      />
      <div
        className={[
          'absolute top-2 left-2 w-7 h-7 rounded-lg backdrop-blur transition-all flex items-center justify-center',
          selected
            ? 'bg-henrik border border-henrik text-white'
            : 'bg-black/50 border border-white/60 text-transparent',
        ].join(' ')}
      >
        {selected ? '✓' : ''}
      </div>
    </div>
  );
}

interface ActionBarProps {
  count: number;
  onCompare: () => void;
  onAddToSet: () => void;
  onClear: () => void;
  addSetBtnRef: React.RefObject<HTMLButtonElement>;
}

function ActionBar({
  count,
  onCompare,
  onAddToSet,
  onClear,
  addSetBtnRef,
}: ActionBarProps) {
  return (
    <div
      className={[
        'fixed bottom-3.5 sm:bottom-7 left-1/2 -translate-x-1/2 z-40',
        'flex items-center gap-2.5 pl-4 pr-3.5 py-3',
        'bg-loupe-bg-2/95 backdrop-blur-2xl border border-loupe-border-2 rounded-full',
        'shadow-[0_20px_60px_-10px_rgba(0,0,0,0.6)]',
        'max-w-[calc(100vw-24px)]',
      ].join(' ')}
    >
      <div className="flex items-center gap-2 text-sm font-medium whitespace-nowrap">
        <strong className="font-display text-lg text-henrik tabular-nums">{count}</strong>
        <span className="hidden sm:inline">selected</span>
      </div>
      <div className="w-px h-5 bg-loupe-border-2" />
      <button
        onClick={onCompare}
        className="bg-transparent text-loupe-text px-3 py-2 rounded-full text-xs font-medium flex items-center gap-2 hover:bg-loupe-bg-3 min-h-[40px] whitespace-nowrap transition-colors"
      >
        <GitCompare className="w-3.5 h-3.5" />
        <span className="hidden sm:inline">Compare</span>
      </button>
      <button
        ref={addSetBtnRef}
        onClick={onAddToSet}
        className="bg-white text-black font-semibold px-3.5 py-2 rounded-full text-xs flex items-center gap-2 hover:bg-neutral-200 min-h-[40px] whitespace-nowrap transition-colors"
      >
        <FolderPlus className="w-3.5 h-3.5" />
        <span>Add<span className="hidden sm:inline"> to set</span></span>
      </button>
      <button
        onClick={onClear}
        aria-label="Clear selection"
        className="text-loupe-text-3 hover:text-loupe-text p-2 transition-colors"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}

function SkeletonMasonry() {
  return (
    <div className="masonry cols-3">
      {Array.from({ length: 18 }).map((_, i) => {
        const tall = i % 5 !== 3;
        return (
          <div
            key={i}
            className="bg-loupe-bg-2 rounded-md animate-pulse"
            style={{ aspectRatio: tall ? '2/3' : '3/2', marginBottom: 8 }}
          />
        );
      })}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="rounded-2xl border border-dashed border-loupe-border-2 p-12 text-center">
      <div className="font-display text-2xl mb-2">No frames match these filters</div>
      <p className="text-loupe-text-2 text-sm">
        Try widening the star range or switching the user filter to All.
      </p>
    </div>
  );
}

function ErrorState({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="rounded-2xl border border-loupe-border-2 p-12 text-center">
      <div className="font-display text-2xl mb-2 text-michelle">Could not load images</div>
      <p className="text-loupe-text-2 text-sm mb-4">
        Make sure the backend is running on port 8090 and that this shoot exists.
      </p>
      <button
        onClick={onRetry}
        className="px-4 py-2 rounded-full bg-white text-black text-xs font-semibold uppercase tracking-wider"
      >
        Retry
      </button>
    </div>
  );
}
